import React from 'react';
import PropTypes from 'prop-types';
import { Grid, ImageList, ImageListItem, ImageListItemBar, Link } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';

import Image from 'components/common/Image';
import VideoImage from './VideoImage';

const cols = 2;
const rows = 2;
const cellHeight = 80;
const cellPadding = 4;
const spacing = cellPadding * 2;
const maxTiles = cols * rows;
const colsOffset = 4;
const rowsOffset = 2;

const useStyles = makeStyles()((theme) => ({
  container: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    ...theme.mixins.scrollBarOverflowYAuto,
    '& > :nth-of-type(1)': {
      order: 0,
      [theme.breakpoints.down('md')]: {
        order: 1,
      },
    },
    '& > :nth-of-type(2)': {
      order: 1,
      [theme.breakpoints.down('md')]: {
        order: 0,
      },
    },
  },
  image: {
    position: 'relative',
    width: 270,
    height: (cellHeight + cellPadding) * rows,
  },
  gridList: {
    margin: 0,
    width: 218,
  },
  gridTileBar: {
    height: '100%',
    textAlign: 'center',
    zIndex: 1000,
    cursor: 'pointer',
  },
}));

const useTileStyles = makeStyles()({
  tile: {
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
  },
});

// TODO: improve tiles generation readability
function EventGallery({ files: [first, ...other], onImageClick }) {
  const { classes, cx } = useStyles();
  const { classes: tileClasses } = useTileStyles();
  const [t] = useTranslation('public');

  const tiles = other.slice(0, maxTiles);
  const hasMore = other.length > maxTiles;
  const handleImageClick = () => {
    onImageClick(first);
  };
  return (
    <Grid className={classes.container} container spacing={1}>
      <Grid item>
        {first.link ? (
          <VideoImage
            file={first}
            variant="rectangle"
            onClick={handleImageClick}
            className={cx(classes.image, tileClasses.tile)}
            component={Image}
          />
        ) : (
          <Image
            src={first.image.original_url}
            alt={first.image.internal_title}
            variant="rectangle"
            onClick={handleImageClick}
            className={cx(classes.image, tileClasses.tile)}
            priority
          />
        )}
      </Grid>
      {tiles.length > 0 && (
        <Grid item>
          <ImageList className={classes.gridList} gap={spacing} rowHeight={cellHeight} cols={cols}>
            {tiles.map((tile, index) => {
              const onClick = () => {
                onImageClick(tile);
              };

              const colSize = tiles.length + index < colsOffset ? 2 : 1;
              const rowSize = tiles.length + index < rowsOffset ? 2 : 1;
              const isLastIndex = tiles.length - 1 === index;

              return (
                <ImageListItem
                  key={tile.id}
                  className={tileClasses.tile}
                  cols={colSize}
                  rows={rowSize}
                  onClick={onClick}
                >
                  {tile.link ? (
                    <VideoImage file={tile} />
                  ) : (
                    <Image src={tile.image.original_url} alt={tile.image.internal_title} layout="fill" priority />
                  )}
                  {hasMore && isLastIndex && (
                    <ImageListItemBar
                      className={classes.gridTileBar}
                      title={
                        <Link variant="button" component="button" color="inherit">
                          {t('common:button.viewAll')}
                        </Link>
                      }
                    />
                  )}
                </ImageListItem>
              );
            })}
          </ImageList>
        </Grid>
      )}
    </Grid>
  );
}

EventGallery.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      order: PropTypes.number.isRequired,
      image: PropTypes.shape({
        id: PropTypes.number.isRequired,
        original_url: PropTypes.string.isRequired,
        internal_title: PropTypes.string,
      }),
    }),
  ).isRequired,
  onImageClick: PropTypes.func.isRequired,
};

export default React.memo(EventGallery);
